import "bulma";
import "./styles/styles.styl";

import _ from "lodash";
import moment from "moment";
import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { Redirect, Route, Switch } from "react-router";
import { ConnectedRouter } from "react-router-redux";
import LoadingIndicator from "./components/LoadingIndicator";
import ServerErrorModal from "./components/ServerErrorModal";
import ICustomer from "./interfaces/ICustomer";
import IKenoDraw from "./interfaces/IKenoDraw";
import IProductPlayType from "./interfaces/IProductPlayType";
import IProductPlayTypeComponent from "./interfaces/IProductPlayTypeComponent";
import DrawPage from "./pages/DrawPage";
import PurchasePage from "./pages/PurchasePage";
import { OLD_DRAWS_LIMIT } from "./settings";
import history from "./utils/history";
import OGSAPI from "./utils/OGSAPI";
import { store } from "./utils/redux";

interface IState {
    currentDraw?: IKenoDraw;
    customer?: ICustomer;
    fetchedCustomer: boolean;
    fetchedDraws: boolean;
    nextDraw?: IKenoDraw;
    pastDraws: {
        [key: string]: IKenoDraw | undefined;
    };
    productPlayType?: IProductPlayType;
    productPlayTypeComponents: IProductPlayTypeComponent[];
    serverError: boolean;
}

class Application extends React.Component<{}, IState> {
    public state: IState = {
        fetchedCustomer: false,
        fetchedDraws: false,
        pastDraws: {},
        productPlayTypeComponents: [],
        serverError: false,
    };
    public componentDidMount(): void {
        const ONE_SECOND = 1000;
        window.setInterval(this.forceUpdate.bind(this), ONE_SECOND);

        OGSAPI.fetchCurrentCustomer().then((customer: ICustomer): void => {
            this.setState({
                customer,
                fetchedCustomer: true,
            });
        }, () => {
            this.setState({
                fetchedCustomer: true,
            });
        });

        this.fetchOldDraws();
        this.fetchDraws();

        OGSAPI.fetchProductPlayTypeByName("Keno").then((productPlayType: IProductPlayType): void => {
            this.setState({ productPlayType });
        }, (): void => {
            this.setState({
                serverError: true,
            });
        });

        OGSAPI.fetchProductPlayTypeComponents({
            productplaytype__name: "Keno",
        }).then((productPlayTypeComponents: IProductPlayTypeComponent[]): void => {
            this.setState({
                productPlayTypeComponents,
            });
        }, (): void => {
            this.setState({
                serverError: true,
            });
        });
    }
    public render(): JSX.Element {
        const {
            customer,
            currentDraw,
            fetchedCustomer,
            nextDraw,
            pastDraws,
            productPlayType,
            productPlayTypeComponents,
            serverError,
        } = this.state;

        return (
            <Provider store={ store }>
                <ConnectedRouter history={ history }>
                    <div className="keno">
                        { serverError ? (
                            <ServerErrorModal />
                        ) : undefined }
                        <div className="wrapper">
                            { (
                                fetchedCustomer &&
                                productPlayType &&
                                nextDraw &&
                                productPlayTypeComponents.length
                            ) ? (
                                <Switch>
                                    <Route exact path="/draw/" render={ (): JSX.Element => (
                                        <DrawPage
                                            customer={ customer }
                                            currentDraw={ currentDraw }
                                            fetchedCustomer={ fetchedCustomer }
                                            nextDraw={ nextDraw }
                                            pastDraws={ pastDraws }
                                            productPlayType={ productPlayType }
                                        />
                                    ) } />
                                    <Route exact path="/purchase/" render={ (): JSX.Element => (
                                        <PurchasePage
                                            customer={ customer }
                                            nextDraw={ nextDraw }
                                            productPlayType={ productPlayType }
                                            productPlayTypeComponents={ productPlayTypeComponents }
                                        />
                                    ) } />
                                    <Route exact path="/" render={ (): JSX.Element => (
                                        <Redirect to="/draw/" />
                                    ) } />
                                    <Route render={ (): JSX.Element => (
                                        <div>Page not found!</div>
                                    ) }></Route>
                                </Switch>
                            ) : (
                                <div className="whole-screen-loading">
                                    <LoadingIndicator />
                                </div>
                            ) }
                        </div>
                    </div>
                </ConnectedRouter>
            </Provider>
        );
    }
    protected fetchDraws(): void {

        // Add current draw to the indexed past draws
        const existingCurrentDraw = this.state.currentDraw;
        if (existingCurrentDraw) {
            const { pastDraws } = this.state;
            this.setState({
                pastDraws: {
                    ...pastDraws,
                    [existingCurrentDraw.id]: existingCurrentDraw,
                },
            });
        }

        // Refresh next draws
        OGSAPI.fetchCurrentAndNextKenoDraws().then(([currentDraw, nextDraw]: IKenoDraw[]) => {
            this.setState({
                currentDraw,
                fetchedDraws: true,
                nextDraw,
            });

            if (!currentDraw || !nextDraw) {
                this.setState({
                    serverError: true,
                });
                return;
            }

            // Set the timeout for the next draw fetch
            const now = moment().unix();

            if (nextDraw) {
                const ONE_SECOND_IN_MS = 1000;
                if (nextDraw.scheduledDrawTimestamp) {
                    const futureSeconds = nextDraw.scheduledDrawTimestamp - now;
                    if (futureSeconds <= 0) {
                        const REFRESH_DRAWS_DELAY_DRAW_TIME_PASSED = 5000;
                        setTimeout(this.fetchDraws.bind(this), REFRESH_DRAWS_DELAY_DRAW_TIME_PASSED);
                    } else {
                        // Wait a little extra
                        const futureMilliseconds = (futureSeconds + 1) * ONE_SECOND_IN_MS;
                        setTimeout(this.fetchDraws.bind(this), futureMilliseconds);
                    }
                } else {
                    const REFRESH_DRAWS_DELAY_UNKNOWN_DRAW_TIME = 10000;
                    setTimeout(this.fetchDraws.bind(this), REFRESH_DRAWS_DELAY_UNKNOWN_DRAW_TIME);
                }
            } else {
                const REFRESH_DRAWS_DELAY_NO_NEXT_DRAW_FOUND = 5000;
                setTimeout(this.fetchDraws.bind(this), REFRESH_DRAWS_DELAY_NO_NEXT_DRAW_FOUND);
            }
        }, (): void => {
            this.setState({
                serverError: true,
            });
        });
    }
    protected fetchOldDraws(): void {
        // Draws from the past
        OGSAPI.fetchKenoDraws({
            limit: OLD_DRAWS_LIMIT + 1,
            sort_by: "-id",
            status__in: "drawn,finalized",
        }).then((pastDraws: IKenoDraw[]): void => {
            const indexedPastDraws = _.keyBy(pastDraws, "id");

            this.setState({
                pastDraws: indexedPastDraws,
            });

        }, (): void => {
            window.alert("Error fetching past Keno draws.");
        });
    }
}

ReactDOM.render(
    <Application />,
    document.getElementById("application"),
);



// WEBPACK FOOTER //
// ./src/application.tsx